import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { changeMyPassword } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { Activity, CalendarDays, Landmark, MessageSquareText, School, ScrollText, ShieldCheck, Store, UserCog, Users } from "lucide-react";
import DioceseSelector from "@/components/DioceseSelector";
import BackofficeShell from "@/components/layout/BackofficeShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const navItems = [
  { href: "/sedec", label: "Utilisateurs", icon: Users },
  { href: "/sedec/suivi", label: "Suivi des écoles", icon: Activity },
  { href: "/sedec/ecoles", label: "Écoles (onboarding)", icon: School },
  { href: "/sedec/vitrines", label: "Vitrines", icon: Store },
  { href: "/sedec/comptes-reversement", label: "Comptes de reversement", icon: Landmark },
  { href: "/sedec/annees", label: "Années scolaires", icon: CalendarDays },
  { href: "/sedec/diffusion", label: "Diffusion", icon: MessageSquareText },
  { href: "/sedec/audit", label: "Audit du diocèse", icon: ScrollText },
];

export default function SedecShell({ children }: { children: React.ReactNode }) {
  const [mdpOpen, setMdpOpen] = useState(false);
  const [actuel, setActuel] = useState("");
  const [nouveau, setNouveau] = useState("");

  const closeMdp = () => {
    setMdpOpen(false);
    setActuel("");
    setNouveau("");
  };

  const changeMdp = useMutation({
    mutationFn: () => changeMyPassword(actuel, nouveau),
    onSuccess: () => {
      toast.success("Mot de passe modifié.");
      closeMdp();
    },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });

  const canSubmitMdp = actuel.length > 0 && nouveau.length >= 8 && !changeMdp.isPending;

  return (
    <>
      <BackofficeShell
        title="CATHO SCHOOL · SEDEC Diocésain"
        icon={ShieldCheck}
        navItems={navItems}
        selector={<DioceseSelector />}
        actions={[
          { label: "Mon compte", icon: UserCog, onClick: () => setMdpOpen(true), testId: "btn-mon-compte" },
        ]}
      >
        {children}
      </BackofficeShell>

      <Dialog
        open={mdpOpen}
        onOpenChange={(o) => {
          /* v8 ignore start -- sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (Échap/overlay) ; la branche o=true est inatteignable */
          if (o) setMdpOpen(true);
          /* v8 ignore stop */
          else closeMdp();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-navy px-6 py-4 text-left">
            <DialogTitle className="font-display text-lg font-bold text-white">Changer mon mot de passe</DialogTitle>
            <DialogDescription className="text-white/70">Saisissez votre mot de passe actuel puis le nouveau (8 caractères minimum).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Mot de passe actuel</Label>
              <Input type="password" value={actuel} onChange={(e) => setActuel(e.target.value)} data-testid="mdp-actuel" />
            </div>
            <div>
              <Label>Nouveau mot de passe (min 8 caractères)</Label>
              <Input type="password" value={nouveau} onChange={(e) => setNouveau(e.target.value)} data-testid="mdp-nouveau" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={closeMdp}>Annuler</Button>
              <Button onClick={() => changeMdp.mutate()} disabled={!canSubmitMdp} className="bg-navy text-white hover:bg-navy/90" data-testid="btn-submit-mdp">
                {changeMdp.isPending ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
